import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../../constants/common'
import { ScreenContainerRef } from '../../../../context'
import {
  useContentOverflow,
  useDateLocale,
  useTimeZone,
} from '../../../../hooks'
import { formatDateRelativeToToday } from '../../../../utils/date'
import { getUserAvatar } from '../../../../utils/user'
import { Avatar } from '../../../Avatar'
import { FloatingTooltip, FloatingTooltipContentText } from '../../../Floating'
import Text from '../../../Text'
import { getEmailMessageStatuses } from '../../Email.utils'
import { EmailPriorityTag } from '../../EmailPriorityTag'
import { MessageStatuses } from '../../MessageStatuses'
import { EmailProfileMessageHeaderActions } from '../EmailProfileMessageHeaderActions'
import { EmailProfileMessageHeaderSenderAdditionalInfoTo } from '../EmailProfileMessageHeaderSenderAdditionalInfoTo'

import * as styles from './EmailProfileMessageHeader.module.css'

function EmailProfileMessageHeader({
  Actions = EmailProfileMessageHeaderActions,
  AdditionalInfo = EmailProfileMessageHeaderSenderAdditionalInfoTo,
  actionsProps,
  additionalInfoProps,
  className = styles.EmailProfileMessageHeader,
  data,
  onClick,
}) {
  const { t } = useTranslation()

  const timezone = useTimeZone()
  const locale = useDateLocale()

  const { overflow: nameOverflow, setContentOverflowRef: setNameOverflowRef } =
    useContentOverflow()
  const { overflow: mailOverflow, setContentOverflowRef: setMailOverflowRef } =
    useContentOverflow()

  const [modal, setModal] = useState(null)

  function handleCloseModal() {
    setModal(null)
  }

  const senderAvatar = getUserAvatar({
    ...data.owner,
    fullname: data.owner.fullname || data.owner.email,
  })

  const created = data.created
    ? formatDateRelativeToToday(data.created, locale, timezone)
    : null

  const statuses = getEmailMessageStatuses({
    error: data.error,
    sending: !data.completed,
    t,
  })

  function handleClick() {
    onClick?.(data.uuid)
  }

  const headerClassName = classNames(className, {
    [styles.clickable]: !!onClick,
  })

  const nameContainerClassName = classNames(styles.nameContainer, {
    [styles.clickable]: !!onClick,
  })

  return (
    <>
      <div className={headerClassName} onClick={handleClick}>
        <Avatar {...senderAvatar} />

        <div className={styles.container}>
          <div className={styles.infoContainer}>
            <div className={styles.mainInfoContainer}>
              {statuses?.length > 0 ? (
                <div className={styles.statuses}>
                  <MessageStatuses statuses={statuses} />
                </div>
              ) : null}
              {data.is_high_priority ? (
                <EmailPriorityTag className={styles.priority} />
              ) : null}
              <div className={styles.topInfoContainer}>
                <FloatingTooltip
                  content={
                    <FloatingTooltipContentText
                      additionalText={`<${data.sender.email || data.owner.email}>`}
                      text={
                        data.sender.name ||
                        data.owner.fullname ||
                        data.sender.email
                      }
                    />
                  }
                  disabled={!nameOverflow && !mailOverflow}
                  placement={FLOATING_ELEMENT_PLACEMENT.top}
                >
                  <div className={nameContainerClassName}>
                    <Text className={styles.name} ref={setNameOverflowRef}>
                      {data.sender.name ||
                        data.owner.fullname ||
                        data.sender.email}
                    </Text>
                    <Text
                      className={styles.mail}
                      ref={setMailOverflowRef}
                    >{`<${data.sender.email}>`}</Text>
                  </div>
                </FloatingTooltip>
                <div className={styles.actionsContainer}>
                  <Text isSecondary className={styles.created}>
                    {created}
                  </Text>

                  {Actions ? <Actions {...actionsProps} data={data} /> : null}
                </div>
              </div>
            </div>
            {AdditionalInfo ? (
              <AdditionalInfo
                {...additionalInfoProps}
                data={data}
                onCloseModal={handleCloseModal}
                onSetModal={setModal}
              />
            ) : null}
          </div>
        </div>
      </div>
      <ScreenContainerRef value={null}>{modal}</ScreenContainerRef>
    </>
  )
}

export default EmailProfileMessageHeader
